import { useNavigate } from "@tanstack/react-router";
import { Check, ChevronDown, Coffee, Plus, User, Users } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAppContexts, type AppContext } from "@/hooks/use-app-context";

function ContextIcon({ kind, className }: { kind: AppContext["kind"]; className?: string }) {
  if (kind === "cafe") return <Coffee className={className} />;
  if (kind === "club") return <Users className={className} />;
  return <User className={className} />;
}

export function ContextSwitcher() {
  const { contexts, managed, active, setActive, ready } = useAppContexts();
  const navigate = useNavigate();

  if (!ready) return null;

  const onSelect = (ctx: AppContext) => {
    setActive(ctx.id);
    if (ctx.kind === "cafe") navigate({ to: "/cafe/dashboard" });
    else navigate({ to: "/dashboard" });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-card/60 px-3 py-1.5 text-sm hover:border-primary/60 hover:text-primary transition max-w-[180px]">
        <ContextIcon kind={active.kind} className="h-4 w-4 text-primary shrink-0" />
        <span className="truncate">{active.name}</span>
        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60 bg-card border-border/60">
        <DropdownMenuLabel className="text-xs uppercase tracking-wider text-muted-foreground">
          Cambiar de contexto
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {contexts.map((ctx) => (
          <DropdownMenuItem
            key={ctx.id}
            onSelect={() => onSelect(ctx)}
            className="gap-2 cursor-pointer"
          >
            <ContextIcon kind={ctx.kind} className="h-4 w-4 text-primary" />
            <span className="flex-1 truncate">{ctx.name}</span>
            {ctx.id === active.id && <Check className="h-4 w-4 text-primary" />}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() => navigate({ to: "/negocios/nuevo-cafe" })}
          className="gap-2 cursor-pointer text-muted-foreground"
        >
          <Plus className="h-4 w-4 text-primary" />
          <span className="flex-1">Registrar café</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => navigate({ to: "/negocios/nuevo-club" })}
          className="gap-2 cursor-pointer text-muted-foreground"
        >
          <Plus className="h-4 w-4 text-primary" />
          <span className="flex-1">Crear club</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
